import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useState } from 'react';

export interface FileUploadProgress {
  percentage: number;
  remainingTime?: number;
}

export interface UseUploadFileVariables {
  file: File;
  uploadURL: string;
}

export const useUploadFile = () => {
  const [progress, setProgress] = useState<FileUploadProgress>();
  const [controller, setController] = useState<AbortController | null>(null);

  const mutation = useMutation({
    mutationFn: async ({ file, uploadURL }: UseUploadFileVariables) => {
      const newController = new AbortController();
      setController(newController);
      const startTime = Date.now();

      try {
        return await axios.put(uploadURL, file, {
          signal: newController.signal,
          onUploadProgress: (event) => {
            if (event?.loaded && event?.total) {
              const elapsedSeconds = (Date.now() - startTime) / 1000;
              const uploadSpeed = event.loaded / elapsedSeconds;
              const remainingTime = (event.total - event.loaded) / uploadSpeed;
              const percentage = ((event.loaded / event.total) * 100).toFixed(0);

              setProgress({
                percentage: Number(percentage),
                remainingTime,
              });
            }
          },
        });
      } catch (error) {
        if (axios.isCancel(error)) {
          mutation.reset();
          return;
        }
        throw error;
      }
    },
  });

  const cancelUpload = () => {
    controller?.abort();
  };

  return {
    ...mutation,
    cancelUpload,
    progress,
    isCancelled: controller?.signal.aborted,
  };
};
